/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Modal, Row, Spinner } from "react-bootstrap";
import { api, ApiError } from "core/api";
import { Button, Table, SvgIcon, Card, Input, Alert, Switch } from "core/components";
import { ChangeOrderStatus, ProjectFormData, ProjectStatus } from "core/schema";
import {
  change_orders_admin_project_path,
  new_change_order_admin_project_path,
  approve_admin_change_order_path,
  reject_admin_change_order_path,
  admin_inventory_items_path,
  admin_tasks_path,
  active_project_tasks_admin_project_path,
  print_admin_change_order_path,
  admin_change_order_path
} from "routes";
import ProjectChangeOrder from "./ProjectChangeOrder";
import moment from "moment";
import formatter from "core/formatter";
import ChangeOrderItems from "./ChangeOrderItems";
import { Controller, useForm } from "react-hook-form";

type ProjectChangeOrdersProps = {
  project: ProjectFormData;
  is_admin_or_architect: boolean;
};

const ProjectChangeOrders = ({ project, is_admin_or_architect }: ProjectChangeOrdersProps): JSX.Element => {
  const {
    control,
    formState: { isSubmitting, isValidating },
    handleSubmit,
    reset
  } = useForm({
    mode: "onChange"
  });
  const [loading, setLoading] = React.useState(false);
  const [changeOrders, setChangeOrders] = React.useState([]);
  const [showNewChangeOrder, setShowNewChangeOrder] = React.useState(false);
  const [changeOrder, setChangeOrder] = React.useState(null);
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);
  const [showChangeOrderModal, setShowChangeOrderModal] = React.useState(false);
  const [showApproveConfirmationModal, setShowApproveConfirmationModal] = React.useState(false);
  const [showRejectConfirmationModal, setShowRejectConfirmationModal] = React.useState(false);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = React.useState(false);
  const [isItemsLoading, setIsItemsLoading] = React.useState(false);
  const [items, setItems] = React.useState([]);
  const [tasks, setTasks] = React.useState([]);
  const [activeProjectTasks, setActiveProjectTasks] = React.useState([]);
  const [changeOrderTasks, setChangeOrderTasks] = React.useState([]);
  const canChangeOrder = is_admin_or_architect && project.status == ProjectStatus.OnGoing;

  const table = React.useRef(null);

  const print = async (change_order): Promise<unknown> => {
    try {
      await api.get(print_admin_change_order_path(change_order));
      window.open(print_admin_change_order_path(change_order), "_blank");
    } catch (error) {
      console.error(error);
    }
  };

  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id"
      },
      {
        id: 1,
        Header: "Change Order ID",
        accessor: "code",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Total Budgeted Amount",
        accessor: "total_budgeted_amount",
        Cell: (props) => {
          const totalBudgetedAmount = formatter.format(props.value);

          return (
            <div title={totalBudgetedAmount} data-bs-toggle="tooltip">
              <p className="text-right text-truncate">{totalBudgetedAmount}</p>
            </div>
          );
        }
      },
      {
        Header: "Requested By",
        accessor: "requested_by",
        Cell: (props) => {
          const requestedBy = props.value;

          return (
            <div title={requestedBy} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{requestedBy}</p>
            </div>
          );
        }
      },
      {
        Header: "Approved By",
        accessor: "approved_by",
        Cell: (props) => {
          const approvedBy = props.value || "-";

          return (
            <div title={approvedBy} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{approvedBy}</p>
            </div>
          );
        }
      },
      {
        Header: "Rejected By",
        accessor: "rejected_by",
        Cell: (props) => {
          const rejectedBy = props.value || "-";

          return (
            <div title={rejectedBy} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{rejectedBy}</p>
            </div>
          );
        }
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: (props) => {
          const status = I18n.t(`activerecord.attributes.change_order.statuses.${props.value}`);

          return (
            <div
              className={`d-flex justify-content-center badge badge--pill badge--${props.value.replace("_", "-")}`}
              title={status}
              data-bs-toggle="tooltip"
            >
              <p className="text-truncate">{status}</p>
            </div>
          );
        }
      },
      {
        Header: "Date Updated",
        accessor: "updated_at",
        Cell: (props) => {
          const updatedAt = props.value ? moment(props.value).format("MM/DD/YYYY hh:mm a") : "-";
          return (
            <div title={updatedAt} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{updatedAt}</p>
            </div>
          );
        }
      },
      {
        Header: "Actions",
        accessor: "",
        Cell: (props) => {
          const { policy, status, has_transactions } = props.row.original;
          const document = props.row.original.documents_attributes[0];
          const isForApproval = status == ChangeOrderStatus.ForApproval;
          const isActive = status == ChangeOrderStatus.Active;

          return (
            <div className="d-flex justify-content-center">
              <a
                className="button button--action button--action-success"
                title="View Change Order"
                data-bs-toggle="tooltip"
                onClick={() => {
                  setChangeOrder(props.row.original);
                  setShowChangeOrderModal(!showChangeOrderModal);
                }}
              >
                <SvgIcon name="view" className="h5" />
              </a>
              {(policy.approve || policy.reject) && isForApproval && (
                <>
                  <a
                    className="button button--action button--action-approve"
                    title="Approve Change Order"
                    data-bs-toggle="tooltip"
                    onClick={() => {
                      setChangeOrder(props.row.original);
                      setShowApproveConfirmationModal(!showApproveConfirmationModal);
                    }}
                  >
                    <SvgIcon name="check" className="h5" />
                  </a>
                  <a
                    className="button button--action button--action-danger"
                    title="Reject Change Order"
                    data-bs-toggle="tooltip"
                    onClick={() => {
                      setChangeOrder(props.row.original);
                      setShowRejectConfirmationModal(!showRejectConfirmationModal);
                    }}
                  >
                    <SvgIcon name="times" className="h5" />
                  </a>
                </>
              )}
              {(document?.file_url || document?.file_name) && (
                <>
                  <a
                    className="a button button--action button--action-info"
                    download={true}
                    target="_blank"
                    rel="noopener"
                    href={document?.file_url || document?.file_name}
                  >
                    <SvgIcon name="download" />
                  </a>
                </>
              )}
              {policy.destroy && (isForApproval || isActive) && !has_transactions && (
                <a
                  className="button button--action button--action-danger"
                  title="Delete Change Order"
                  data-bs-toggle="tooltip"
                  onClick={() => {
                    setChangeOrder(props.row.original);
                    setShowDeleteConfirmationModal(!showDeleteConfirmationModal);
                  }}
                >
                  <SvgIcon name="delete" className="h6" />
                </a>
              )}
              {/* <a
                className="button button--action button--action-primary"
                title="Print Change Order"
                data-bs-toggle="tooltip"
                onClick={() => {
                  print(props.row.original);
                }}
              >
                <SvgIcon name="printer" className="h5" />
              </a> */}
            </div>
          );
        }
      }
    ],
    []
  );

  const loader = () => {
    return (
      <div className="d-flex justify-content-center align-items-center h-100 pt-lg-8 pb-lg-8">
        <Spinner animation="border" role="status" size="lg" variant="brand">
          <span className="visually-hidden">Loading Project Change Orders...</span>
        </Spinner>
      </div>
    );
  };

  const noContent = () => {
    return (
      <Container fluid={true} className="text-center pt-lg-8 pb-lg-8">
        <Row className="mb-8">
          <Col>
            <SvgIcon name="clipboard-list" className="display-icon text-gray" />
          </Col>
        </Row>
        <Row>
          <Col>
            <h5>No past change orders.</h5>
          </Col>
        </Row>
      </Container>
    );
  };

  const dataFilter = () => {
    return (
      <Row className="mb-3">
        <Col sm="6">
          <Input placeholder="Search for Change Order ID" onChange={(e) => table.current.setFilter(1, e.target.value)} />
        </Col>
      </Row>
    );
  };

  const dataTable = () => {
    return (
      <Row>
        <Col>
          <Table columns={columns} data={changeOrders} ref={table} />
        </Col>
      </Row>
    );
  };

  const content = () => {
    return showNewChangeOrder ? (
      <ProjectChangeOrder
        changeOrder={changeOrder}
        project={project}
        showNewChangeOrder={showNewChangeOrder}
        setShowNewChangeOrder={setShowNewChangeOrder}
        tasks={tasks}
        items={items}
        is_admin_or_architect={is_admin_or_architect}
        changeOrderTasks={changeOrderTasks}
        isItemsLoading={isItemsLoading}
      />
    ) : changeOrders.length > 0 ? (
      <>
        {dataFilter()}
        {dataTable()}
      </>
    ) : (
      noContent()
    );
  };

  const getChangeOrders = async () => {
    try {
      if (!canChangeOrder) setLoading(true);
      const response = await api.get(change_orders_admin_project_path(project.id));
      setChangeOrders(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      if (!canChangeOrder) setLoading(false);
    }
  };

  const newChangeOrderTasks = (type) => {
    if (type == "add") {
      return [
        {
          id: null,
          change_order_id: null,
          task_id: tasks.find((task) => task.code == "CHG")?.id,
          change_order_task_items_attributes: [
            {
              item_id: ""
            }
          ]
        }
      ];
    } else {
      const changeOrderTasks = activeProjectTasks.map((projectTask) => {
        return {
          id: null,
          change_order_id: null,
          task_id: projectTask.task_id,
          change_order_task_items_attributes: projectTask.project_task_items_attributes.map((projectTaskItem) => {
            return {
              id: null,
              change_order_task_id: null,
              task_item_id: projectTaskItem.id,
              item_id: projectTaskItem.item_id,
              budgeted_quantity: projectTaskItem.budgeted_quantity,
              budgeted_unit_rate: projectTaskItem.budgeted_unit_rate,
              unit_measure_id: projectTaskItem.unit_measure_id,
              actual_quantity: projectTaskItem.actual_quantity,
              can_change_budgeted_quantity: projectTaskItem.can_change_budgeted_quantity,
              type
            };
          })
        };
      });
      setChangeOrderTasks(changeOrderTasks);

      return changeOrderTasks;
    }
  };

  const newChangeOrder = async (type) => {
    try {
      const response = await api.get(new_change_order_admin_project_path(project.id));
      setChangeOrder({
        ...response.data,
        type,
        change_order_type: type,
        change_order_tasks_attributes: newChangeOrderTasks(type)
      });
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  const save = async (data) => {
    setFormErrors([]);

    try {
      await api.put(admin_change_order_path(data.id), data);
      getChangeOrders().catch(console.error);
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    } finally {
      setShowChangeOrderModal(!showChangeOrderModal);
    }
  };

  const onError = (data) => {
    console.error(data);
  };

  const approve = async (id): Promise<unknown> => {
    setFormErrors([]);
    try {
      await api.put(approve_admin_change_order_path(id));
      getChangeOrders().catch(console.error);
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    } finally {
      setShowApproveConfirmationModal(!showApproveConfirmationModal);
    }
  };

  const reject = async (id): Promise<unknown> => {
    setFormErrors([]);
    try {
      await api.put(reject_admin_change_order_path(id));
      getChangeOrders().catch(console.error);
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    } finally {
      setShowRejectConfirmationModal(!showRejectConfirmationModal);
    }
  };

  const deleteChangeOrder = React.useCallback(async (id) => {
    setFormErrors([]);
    try {
      const response = await api.deleteItem(admin_change_order_path(id));
      setChangeOrders((changeOrders) => changeOrders.filter((changeOrder) => changeOrder.id != response.data.id));
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    } finally {
      setShowDeleteConfirmationModal((value) => !value);
    }
  }, []);

  const actions = () => {
    return showNewChangeOrder ? (
      <></>
    ) : (
      <Row>
        <Col lg={6} className="d-flex">
          {formErrors && formErrors.length != 0 && (
            <Alert>
              {formErrors.map((error, index) => {
                return <div key={index}>{error}</div>;
              })}
            </Alert>
          )}
        </Col>
        {canChangeOrder && !loading && (
          <Col lg={6} className="d-flex justify-content-end align-items-start">
            <Button
              className="button--brand mr-3"
              icon="plus"
              title="New Change Order Addition"
              type="button"
              onClick={() => {
                newChangeOrder("add")
                  .then(() => {
                    setShowNewChangeOrder(!showNewChangeOrder);
                  })
                  .catch(console.error);
              }}
            />
            <Button
              className="button--brand"
              icon="plus"
              title="New Change Order Replacement"
              type="button"
              onClick={() => {
                newChangeOrder("replace")
                  .then(() => {
                    setShowNewChangeOrder(!showNewChangeOrder);
                  })
                  .catch(console.error);
              }}
            />
          </Col>
        )}
      </Row>
    );
  };

  const getTasks = async () => {
    try {
      const response = await api.get(admin_tasks_path());
      setTasks(response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  const getItems = async () => {
    try {
      setIsItemsLoading(true);
      const response = await api.get(admin_inventory_items_path());
      setItems(response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    } finally {
      setIsItemsLoading(false);
    }
  };

  const getActiveProjectTasks = async () => {
    try {
      setLoading(true);
      const response = await api.get(active_project_tasks_admin_project_path(project.id));
      setActiveProjectTasks(response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    } finally {
      setLoading(false);
    }
  };

  const canUpdate = (changeOrder) => {
    return [ChangeOrderStatus.ForApproval, ChangeOrderStatus.Active].includes(changeOrder?.status) && changeOrder?.policy?.update;
  };

  React.useEffect(() => {
    getChangeOrders().catch(console.error);

    if (canChangeOrder) {
      getTasks().catch(console.error);
      getItems().catch(console.error);
      getActiveProjectTasks().catch(console.error);
    }
  }, [showNewChangeOrder]);

  return (
    <React.StrictMode>
      <Container fluid={true}>
        {actions()}
        <Row>
          <Col>
            <Card.Body>{loading ? loader() : content()}</Card.Body>
          </Col>
        </Row>
      </Container>
      <Modal dialogClassName="modal-dialog-centered modal-xl" show={showChangeOrderModal}>
        <Modal.Header style={{ display: "block" }}>
          {canUpdate(changeOrder) && (
            <Controller
              name="include_budget"
              control={control}
              render={({ field }) => (
                <Switch
                  {...field}
                  label="Include Budget"
                  info="Include or exclude change order to the budget amounts in the Actual vs Budget Report and Performance Evaluation Report"
                  checked={field.value}
                  value={field.value?.toString()}
                />
              )}
            />
          )}
          <p>{`Change Order: ${changeOrder?.code}`}</p>
          <div title={changeOrder?.description} data-bs-toggle="tooltip">
            <p className="text-truncate">{`Description: ${changeOrder?.description}`}</p>
          </div>
        </Modal.Header>
        <Modal.Body>
          <ChangeOrderItems changeOrder={changeOrder} reset={reset} />
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowChangeOrderModal(!showChangeOrderModal);
            }}
            icon="times"
            title="Close"
          />
          {canUpdate(changeOrder) && (
            <Button
              className="button--brand"
              icon="save"
              title="Save"
              disabled={isSubmitting || isValidating}
              onClick={() => {
                handleSubmit(async (data) => await save(data), onError)();
              }}
            />
          )}
        </Modal.Footer>
      </Modal>
      <Modal dialogClassName="modal-dialog-centered" show={showApproveConfirmationModal}>
        <Modal.Header>{`Approve Change Order: ${changeOrder?.code}`}</Modal.Header>
        <Modal.Body>{`Are you sure you want to approve change order: ${changeOrder?.code}?`}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowApproveConfirmationModal(!showApproveConfirmationModal);
            }}
            icon="times"
            title="Cancel"
          />
          <Button
            className="button--brand"
            icon="save"
            title="Yes, approve change order"
            onClick={() => {
              approve(changeOrder.id).catch(console.error);
            }}
          />
        </Modal.Footer>
      </Modal>
      <Modal dialogClassName="modal-dialog-centered" show={showRejectConfirmationModal}>
        <Modal.Header>{`Reject Change Order: ${changeOrder?.code}`}</Modal.Header>
        <Modal.Body>{`Are you sure you want to reject change order: ${changeOrder?.code}?`}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowRejectConfirmationModal(!showRejectConfirmationModal);
            }}
            icon="times"
            title="Cancel"
          />
          <Button
            className="button--danger"
            icon="save"
            title="Yes, reject change order"
            onClick={() => {
              reject(changeOrder.id).catch(console.error);
            }}
          />
        </Modal.Footer>
      </Modal>
      <Modal dialogClassName="modal-dialog-centered" show={showDeleteConfirmationModal}>
        <Modal.Header>{`Delete Change Order: ${changeOrder?.code}`}</Modal.Header>
        <Modal.Body>{`Are you sure you want to delete change order: ${changeOrder?.code}?`}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowDeleteConfirmationModal(!showDeleteConfirmationModal);
            }}
            icon="times"
            title="Cancel"
          />
          <Button
            className="button--danger"
            icon="delete"
            title="Yes, delete change order."
            onClick={() => {
              deleteChangeOrder(changeOrder.id).catch(console.error);
            }}
          />
        </Modal.Footer>
      </Modal>
    </React.StrictMode>
  );
};

export default ProjectChangeOrders;
